import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit, Trash2, Eye, DollarSign, Users, MapPin, Clock, Save, X, Upload, Check, AlertCircle, Search, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EventsTab = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [venues, setVenues] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showStats, setShowStats] = useState(null);
  const [eventStats, setEventStats] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: { es: '', en: '' },
    description: { es: '', en: '' },
    slug: '',
    date: '',
    start_time: '',
    end_time: '',
    venue_id: '',
    venue_name: '',
    venue_address: '',
    city: '',
    category: '',
    access_levels: [{ name: 'General', price: 0, capacity: 100, currency: 'MXN', benefits: [] }],
    cover_image: '',
    status: 'draft',
    access_rules: { es: '', en: '' },
    operational_notes: '',
    featured: false
  });

  const categories = [
    { value: 'concierto', label: 'Concierto' },
    { value: 'festival', label: 'Festival' },
    { value: 'privado', label: 'Evento Privado' },
    { value: 'corporativo', label: 'Corporativo' },
    { value: 'experiencia', label: 'Experiencia' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Borrador', color: 'text-gray-400' },
    { value: 'published', label: 'Publicado', color: 'text-green-400' },
    { value: 'cancelled', label: 'Cancelado', color: 'text-red-400' },
    { value: 'completed', label: 'Completado', color: 'text-blue-400' }
  ];

  useEffect(() => {
    fetchEvents();
    fetchVenues();
  }, [statusFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/api/admin/events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.items || []);
    } catch (error) {
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/api/public/venues?limit=100`);
      setVenues(response.data.items || []);
    } catch (error) {
      console.error('Error fetching venues');
    }
  };

  const fetchEventStats = async (eventId) => {
    try {
      const response = await axios.get(`${API}/api/admin/events/${eventId}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventStats(response.data);
      setShowStats(eventId);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    }
  };

  const handleVenueSelect = (venueId) => {
    const venue = venues.find(v => v.venue_id === venueId);
    if (venue) {
      setForm(prev => ({
        ...prev,
        venue_id: venueId,
        venue_name: venue.name?.es || '',
        venue_address: venue.address || '',
        city: venue.city || ''
      }));
    }
  };

  const handleAddAccessLevel = () => {
    setForm(prev => ({
      ...prev,
      access_levels: [...prev.access_levels, { name: '', price: 0, capacity: 0, currency: 'MXN', benefits: [] }]
    }));
  };

  const handleRemoveAccessLevel = (index) => {
    if (form.access_levels.length > 1) {
      setForm(prev => ({
        ...prev,
        access_levels: prev.access_levels.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAccessLevelChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      access_levels: prev.access_levels.map((level, i) => 
        i === index ? { ...level, [field]: value } : level
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const eventData = {
        ...form,
        slug: form.slug || form.name.es.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      };

      if (editingEvent) {
        await axios.put(`${API}/api/admin/events/${editingEvent.event_id}`, eventData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Evento actualizado');
      } else {
        await axios.post(`${API}/api/admin/events`, eventData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Evento creado');
      }
      
      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (event) => {
    setForm({
      name: event.name || { es: '', en: '' },
      description: event.description || { es: '', en: '' },
      slug: event.slug || '',
      date: event.date || '',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      venue_id: event.venue_id || '',
      venue_name: event.venue_name || '',
      venue_address: event.venue_address || '',
      city: event.city || '',
      category: event.category || '',
      access_levels: event.access_levels?.length > 0 ? event.access_levels : [{ name: 'General', price: 0, capacity: 100, currency: 'MXN', benefits: [] }],
      cover_image: event.cover_image || '',
      status: event.status || 'draft',
      access_rules: event.access_rules || { es: '', en: '' },
      operational_notes: event.operational_notes || '',
      featured: event.featured || false
    });
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('¿Estás seguro de eliminar este evento?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Evento eliminado');
      fetchEvents();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setForm({
      name: { es: '', en: '' },
      description: { es: '', en: '' },
      slug: '',
      date: '',
      start_time: '',
      end_time: '',
      venue_id: '',
      venue_name: '',
      venue_address: '',
      city: '',
      category: '',
      access_levels: [{ name: 'General', price: 0, capacity: 100, currency: 'MXN', benefits: [] }],
      cover_image: '',
      status: 'draft',
      access_rules: { es: '', en: '' },
      operational_notes: '',
      featured: false
    });
  };

  const filteredEvents = events.filter(event => {
    const name = event.name?.es || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-amber-500" />
            Eventos
          </h2>
          <p className="text-gray-400 text-sm">Gestiona eventos y venta de accesos</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Evento
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar eventos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-zinc-800 text-white pl-10 pr-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700"
        >
          <option value="">Todos los estados</option>
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 rounded-xl">
          <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-gray-400">No hay eventos</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredEvents.map((event) => {
            const totalCapacity = event.total_capacity || 0;
            const totalSold = event.total_sold || 0;
            const percentSold = totalCapacity > 0 ? (totalSold / totalCapacity) * 100 : 0;
            
            return (
              <div key={event.event_id} className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="w-full md:w-48 h-32 md:h-auto bg-zinc-800 flex-shrink-0">
                    {event.cover_image ? (
                      <img src={event.cover_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Calendar className="w-8 h-8 text-zinc-600" />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-white">{event.name?.es || 'Sin nombre'}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            event.status === 'published' ? 'bg-green-500/20 text-green-400' :
                            event.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            event.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {statusOptions.find(s => s.value === event.status)?.label || event.status}
                          </span>
                          {event.featured && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Destacado</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-400">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(event.date)}
                          </span>
                          {event.start_time && (
                            <span className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {event.start_time}
                            </span>
                          )}
                          {event.venue_name && (
                            <span className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {event.venue_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => fetchEventStats(event.event_id)}
                          className="p-2 text-gray-400 hover:text-amber-500 transition-colors"
                          title="Ver estadísticas"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </button>
                        <a
                          href={`/eventos/${event.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                          title="Ver página"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleEdit(event)}
                          className="p-2 text-gray-400 hover:text-amber-500 transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(event.event_id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Sales Progress */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-400">
                          <Users className="w-4 h-4 inline mr-1" />
                          {totalSold} / {totalCapacity} vendidos
                        </span>
                        <span className="text-amber-400 font-medium">{percentSold.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all"
                          style={{ width: `${Math.min(percentSold, 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Access Levels Preview */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {event.access_levels?.map((level, i) => (
                        <span key={i} className="text-xs bg-zinc-800 text-gray-300 px-2 py-1 rounded">
                          {level.name}: ${level.price?.toLocaleString()} ({level.sold || 0}/{level.capacity})
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Event Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 rounded-2xl max-w-4xl w-full my-8 border border-zinc-800">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingEvent(null); }} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Nombre (ES) *</label>
                  <input
                    type="text"
                    value={form.name.es}
                    onChange={(e) => setForm(prev => ({ ...prev, name: { ...prev.name, es: e.target.value } }))}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Nombre (EN)</label>
                  <input
                    type="text"
                    value={form.name.en}
                    onChange={(e) => setForm(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Fecha *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Hora inicio</label>
                  <input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Hora fin</label>
                  <input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm(prev => ({ ...prev, end_time: e.target.value }))}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Venue</label>
                  <select
                    value={form.venue_id}
                    onChange={(e) => handleVenueSelect(e.target.value)}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                  >
                    <option value="">Seleccionar venue...</option>
                    {venues.map(venue => (
                      <option key={venue.venue_id} value={venue.venue_id}>{venue.name?.es}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Categoría</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                  >
                    <option value="">Seleccionar...</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Descripción (ES)</label>
                <textarea
                  value={form.description.es}
                  onChange={(e) => setForm(prev => ({ ...prev, description: { ...prev.description, es: e.target.value } }))}
                  rows={3}
                  className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none resize-none"
                />
              </div>

              {/* Access Levels */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-gray-400 text-sm font-medium">Niveles de Acceso</label>
                  <button
                    type="button"
                    onClick={handleAddAccessLevel}
                    className="text-amber-500 text-sm hover:text-amber-400"
                  >
                    + Agregar nivel
                  </button>
                </div>
                <div className="space-y-3">
                  {form.access_levels.map((level, index) => (
                    <div key={index} className="bg-zinc-800 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-medium">Nivel {index + 1}</span>
                        {form.access_levels.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAccessLevel(index)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <input
                          type="text"
                          value={level.name}
                          onChange={(e) => handleAccessLevelChange(index, 'name', e.target.value)}
                          placeholder="Nombre (ej: VIP)"
                          className="bg-zinc-700 text-white px-3 py-2 rounded-lg border border-zinc-600 focus:border-amber-500 focus:outline-none text-sm"
                        />
                        <input
                          type="number"
                          value={level.price}
                          onChange={(e) => handleAccessLevelChange(index, 'price', parseFloat(e.target.value) || 0)}
                          placeholder="Precio"
                          className="bg-zinc-700 text-white px-3 py-2 rounded-lg border border-zinc-600 focus:border-amber-500 focus:outline-none text-sm"
                        />
                        <input
                          type="number"
                          value={level.capacity}
                          onChange={(e) => handleAccessLevelChange(index, 'capacity', parseInt(e.target.value) || 0)}
                          placeholder="Capacidad"
                          className="bg-zinc-700 text-white px-3 py-2 rounded-lg border border-zinc-600 focus:border-amber-500 focus:outline-none text-sm"
                        />
                        <select
                          value={level.currency}
                          onChange={(e) => handleAccessLevelChange(index, 'currency', e.target.value)}
                          className="bg-zinc-700 text-white px-3 py-2 rounded-lg border border-zinc-600 focus:border-amber-500 focus:outline-none text-sm"
                        >
                          <option value="MXN">MXN</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Imagen de portada (URL)</label>
                <input
                  type="url"
                  value={form.cover_image}
                  onChange={(e) => setForm(prev => ({ ...prev, cover_image: e.target.value }))}
                  placeholder="https://..."
                  className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Estado</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                  >
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.featured}
                      onChange={(e) => setForm(prev => ({ ...prev, featured: e.target.checked }))}
                      className="sr-only"
                    />
                    <div className={`w-10 h-6 rounded-full transition-colors ${form.featured ? 'bg-amber-500' : 'bg-zinc-700'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white mt-1 transition-transform ${form.featured ? 'translate-x-5' : 'translate-x-1'}`} />
                    </div>
                    <span className="ml-3 text-gray-400">Destacado</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Notas operativas (interno)</label>
                <textarea
                  value={form.operational_notes}
                  onChange={(e) => setForm(prev => ({ ...prev, operational_notes: e.target.value }))}
                  rows={2}
                  className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none resize-none"
                  placeholder="Notas internas para el equipo..."
                />
              </div>
            </form>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingEvent(null); }}
                className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingEvent ? 'Actualizar' : 'Crear'} Evento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStats && eventStats && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowStats(null)}>
          <div className="bg-zinc-900 rounded-2xl max-w-2xl w-full border border-zinc-800" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-amber-500" />
                Estadísticas del Evento
              </h3>
              <button onClick={() => setShowStats(null)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <h4 className="text-lg text-white mb-4">{eventStats.event?.name?.es}</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <DollarSign className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">${eventStats.stats?.total_revenue?.toLocaleString()}</p>
                  <p className="text-gray-400 text-sm">Ingresos</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{eventStats.stats?.total_tickets}</p>
                  <p className="text-gray-400 text-sm">Tickets</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <Check className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{eventStats.stats?.checked_in}</p>
                  <p className="text-gray-400 text-sm">Check-ins</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4 text-center">
                  <AlertCircle className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{eventStats.stats?.attendance_rate?.toFixed(0)}%</p>
                  <p className="text-gray-400 text-sm">Asistencia</p>
                </div>
              </div>
              
              <h5 className="text-white font-medium mb-3">Ventas por nivel</h5>
              <div className="space-y-2">
                {Object.entries(eventStats.stats?.sales_by_level || {}).map(([name, data]) => (
                  <div key={name} className="bg-zinc-800 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-white">{name}</span>
                      <span className="text-amber-400">${data.revenue?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-amber-500"
                          style={{ width: `${data.capacity > 0 ? (data.sold / data.capacity) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="text-gray-400 text-sm">{data.sold}/{data.capacity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsTab;
